import { useState, type CSSProperties } from "react";
import { cn } from "@/lib/utils";
import { formatstorageUrl } from "@/utils/formatters";
import { brandInitials, isGeneratedAvatar } from "@/utils/brandLogo";

interface BrandLogoProps {
  src?: string | null;
  /** Company name; source of the initials and the alt text. */
  name?: string | null;
  /**
   * The brand's primary colour as a hex string when the page already knows
   * it (public join page, previews). Omitted, the initials use the runtime
   * brand tokens, which the white-label theme layer already sets.
   */
  primaryColor?: string | null;
  /** Size and shape; defaults to a full-size circle. */
  className?: string;
  imgClassName?: string;
  initialsClassName?: string;
}

/**
 * A brand's logo with an initials fallback in the brand's own colour. One
 * component for every place a company logo appears so no surface falls back
 * to a hardcoded palette on a white-labelled portal.
 */
export function BrandLogo({
  src,
  name,
  primaryColor,
  className = "w-full h-full rounded-full",
  imgClassName,
  initialsClassName,
}: BrandLogoProps) {
  const [failed, setFailed] = useState(false);
  const showImage = !failed && !isGeneratedAvatar(src);
  const label = name?.trim() || "Brand";

  if (showImage) {
    return (
      <img
        src={formatstorageUrl(src as string)}
        alt={label}
        onError={() => setFailed(true)}
        className={cn("object-cover", className, imgClassName)}
      />
    );
  }

  const style: CSSProperties | undefined = primaryColor
    ? { backgroundColor: primaryColor, color: "#fff" }
    : undefined;

  return (
    <div
      role="img"
      aria-label={label}
      style={style}
      className={cn(
        "flex items-center justify-center font-bold select-none",
        !primaryColor && "bg-brand-600 text-brand-contrast",
        className,
        initialsClassName
      )}
    >
      {brandInitials(name)}
    </div>
  );
}
